/**
 * Canonical catalog of classroom page *types* — the one source of truth shared
 * by the API (layout validation + default seeding) and the web app (registry +
 * nav + settings). The web registry attaches React components to these keys;
 * the API never imports React, so the key/placement/dated metadata lives here.
 *
 * Two orthogonal axes per page:
 *   - placement: where it surfaces in the live experience + settings.
 *       'pinned'          → fixed bookend (home left, search right); never in
 *                           the customizable layout.
 *       'morning-meeting' → default member of the Morning Meeting section.
 *       'standalone'      → its own top-level/class-level tab (YouTube, Links).
 *   - dated: whether the page has per-day content that carries forward and
 *       feeds the History "daily diary". Independent of placement.
 */

export type PagePlacement = 'pinned' | 'morning-meeting' | 'standalone';

export interface PageCatalogEntry {
  key: string;
  placement: PagePlacement;
  /** Has per-day content (carry-forward + History diary). */
  dated: boolean;
}

export const MORNING_MEETING_SECTION_KEY = 'morning-meeting';
export const MORNING_MEETING_SECTION_NAME = 'Morning Meeting';

export const PAGE_CATALOG: readonly PageCatalogEntry[] = [
  { key: 'home', placement: 'pinned', dated: true },
  { key: 'calendar', placement: 'morning-meeting', dated: true },
  { key: 'day-of-school', placement: 'morning-meeting', dated: true },
  { key: 'number', placement: 'morning-meeting', dated: false },
  { key: 'weather', placement: 'morning-meeting', dated: true },
  { key: 'feelings', placement: 'morning-meeting', dated: false },
  { key: 'question', placement: 'morning-meeting', dated: true },
  { key: 'pledges', placement: 'morning-meeting', dated: false },
  { key: 'youtube', placement: 'standalone', dated: false },
  { key: 'links', placement: 'standalone', dated: false },
  { key: 'search', placement: 'pinned', dated: false },
];

const BY_KEY = new Map(PAGE_CATALOG.map((p) => [p.key, p]));

export function getPageCatalogEntry(key: string): PageCatalogEntry | undefined {
  return BY_KEY.get(key);
}

export function isKnownPageKey(key: string): boolean {
  return BY_KEY.has(key);
}

/** Page keys with the given placement, in catalog order. */
export function pageKeysByPlacement(placement: PagePlacement): string[] {
  return PAGE_CATALOG.filter((p) => p.placement === placement).map((p) => p.key);
}

/** Shape of the customizable layout middle (no pinned pages). */
export interface LayoutShape {
  sections: { key: string; name: string; pages: string[] }[];
  standalone: string[];
}

/**
 * Default layout for a classroom that has never been customized: every
 * Morning Meeting page enabled in catalog order, every standalone page
 * enabled. Pinned pages (home/search) are not part of the layout.
 */
export function defaultLayout(): LayoutShape {
  return {
    sections: [
      {
        key: MORNING_MEETING_SECTION_KEY,
        name: MORNING_MEETING_SECTION_NAME,
        pages: pageKeysByPlacement('morning-meeting'),
      },
    ],
    standalone: pageKeysByPlacement('standalone'),
  };
}

/**
 * Sanitize an arbitrary layout: drop unknown and pinned keys everywhere,
 * de-dupe, and ensure the result is well-formed. Order is preserved. Used by
 * the API before persisting and as a defensive read transform.
 */
export function sanitizeLayout(input: LayoutShape): LayoutShape {
  const seen = new Set<string>();
  const keep = (key: string): boolean => {
    const entry = BY_KEY.get(key);
    if (!entry || entry.placement === 'pinned') return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  };
  return {
    sections: (input.sections ?? []).map((s) => ({
      key: s.key,
      name: s.name,
      pages: (s.pages ?? []).filter(keep),
    })),
    standalone: (input.standalone ?? []).filter(keep),
  };
}
